import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: unknown) => void
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: unknown
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // Log error for debugging
    // console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler
    this.props.onError?.(error, errorInfo)

    // Send to error tracking service in production
    if (import.meta.env.PROD) {
      this.logErrorToService(error, errorInfo)
    }
  }

  private logErrorToService(error: Error, errorInfo: unknown) {
    // In a real app, you would send this to Sentry, LogRocket, etc.
    const errorData = {
      message: error.message,
      stack: error.stack,
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      version: import.meta.env.__APP_VERSION__ || '1.0.0',
    }

    // Example: Send to your API
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorData),
    }).catch(() => {
      // Error logging failed
    })
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  private handleReportError = () => {
    if (this.state.error) {
      // Open email client with error details
      const subject = encodeURIComponent('Error Report - Wunschparadies')
      const body = encodeURIComponent(`
Error Details:
${this.state.error.message}

Stack Trace:
${this.state.error.stack}

URL: ${window.location.href}
User Agent: ${navigator.userAgent}
      `)

      window.open(
        `mailto:support@wunschparadies.de?subject=${subject}&body=${body}`
      )
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple/5 to-brand-pink/5">
          <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-xl border border-brand-purple/20">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Oops! Etwas ist schiefgelaufen
              </h2>

              <p className="text-gray-600 mb-6">
                Es tut uns leid, aber es ist ein unerwarteter Fehler
                aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie
                uns, wenn das Problem weiterhin besteht.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="mb-4 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                    Fehlerdetails (nur in Entwicklung)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                    {this.state.error.message}
                    {'\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 px-4 py-2 bg-brand-purple text-white rounded hover:bg-brand-purple/90 transition-colors"
                >
                  Erneut versuchen
                </button>

                <button
                  onClick={this.handleReportError}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Fehler melden
                </button>
              </div>

              <div className="mt-4 text-xs text-gray-500">
                <p>
                  Fehler-ID:{' '}
                  {this.state.error?.message?.substring(0, 8) || 'unknown'}
                </p>
                <p>Version: {import.meta.env.__APP_VERSION__ || '1.0.0'}</p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
